import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type Lang = "en" | "sl";

const translations = {
  en: {
    // Nav
    navHome: "Home",
    navSolutions: "Solutions",
    navServices: "Solutions",
    offerServices: "Services",
    offerProducts: "Standard Products",
    offerSpecialMachines: "Special Machines",
    offerSpareParts: "Spare Parts",
    offerConsumables: "Consumables",
    offerGastroMedicine: "Gastronomy & Medicine",
    navHowItWorks: "How It Works",
    navReferences: "References",
    refProjects: "Projects",
    refUsers: "Users / Clients",
    refTestimonials: "Testimonials",
    refPartners: "Partners",
    navKnowledge: "Knowledge Hub",
    navFAQ: "FAQ",
    navAbout: "About Us",
    navContact: "Contact",
    sendInquiry: "Send Inquiry",
    solGastronomy: "Gastronomy & Medicine",
    solAutomotive: "Automotive Industry",
    solRailway: "Railway Industry",
    solElectronics: "Electronics Industry",
    solFences: "Meshes & Fences",
    solLocks: "Locks",
    solRobotics: "Robotics",
    solWhiteGoods: "White Goods",

    // Solution sub-items
    solGastSinks: "Spot welding machine for welding sinks into counters",
    solGastPillow: "Seam welding machine for welding pillow plates",
    solRailSpotGuns: "Robotic resistance spot welding guns",
    solElecBattery: "Microspot battery welder",
    solLocksWelding: "Resistance welding machine for lock-welding",
    solRoboticSpotGuns: "Robotic resistance spot welding guns",
    solTensionRingWelding: "Tension Ring Welding",
    solBarrelHandleWelding: "Welding machine for barrel handles",
    solTappingBarrelWelding: "Welding machine for tapping barrels",

    // Product categories & sub-items
    prodSpotWelding: "Spot welding",
    prodSpotWeldingCategory: "Spot Welding",
    prodPedestalWelders: "Industrial Pedestal Welders",
    prodMultiSpot: "Multi-spot welding machine",
    prodProjectionWelding: "Projection welding",
    prodProjectionWelders: "Industrial Projection Welders",
    prodSeamWelding: "Seam welding",
    prodSeamWelders: "Industrial Seam Welders",
    prodMicroWelding: "Micro welding",
    prodWeldingHeads: "Welding Heads",
    prodWeldingPincers: "Welding Pincers",
    prodCompactingTools: "Compacting Tools",
    prodBatteryProcessing: "Battery and accumulator processing",
    prodWorkstations: "Workstations",

    // Hero
    heroEyebrow: "Resistance Welding Machine Manufacturer",
    heroTitle1: "Welding machines engineered for ",
    heroTitleHighlight: "your process",
    heroSub: "From cost-optimized welding machines to fully customized premium solutions — engineered to fit your process, not a catalog.",
    viewSolutions: "View Solutions",

    // Benefits
    benefitsEyebrow: "Why Kočevar",
    benefitsTitle: "Results that matter in production",
    benefit1Title: "Stable Weld Quality",
    benefit1Desc: "Consistent, repeatable weld results across every production cycle — reducing scrap and rework.",
    benefit2Title: "Application-Driven Design",
    benefit2Desc: "Every machine is engineered around your specific welding application, materials, and production requirements.",
    benefit3Title: "Process Repeatability",
    benefit3Desc: "Precise control of welding parameters ensures uniform output, shift after shift.",
    benefit4Title: "Reliable Local Support",
    benefit4Desc: "In-house engineering and technical service — fast response, no middlemen.",

    // Featured Projects
    fpEyebrow: "Featured Projects",
    fpTitle: "Real-world welding solutions in action",
    fp1Title: "Spot Welding Sinks into Countertops",
    fp1Desc: "Custom-built welding machines for the gastronomy industry — integrating stainless steel sinks into professional countertops with precision seam and spot welding.",
    fp1Industry: "Gastronomy & Medicine",
    fp2Title: "Robotic Resistance Spot Welding Guns",
    fp2Desc: "High-performance welding guns designed for robotic cells in automotive and railway production — engineered for speed, repeatability, and minimal maintenance.",
    fp2Industry: "Automotive & Railway",
    fp3Title: "Lock Welding Solutions",
    fp3Desc: "Custom resistance welding machines for high-volume lock and latch production — engineered for precision, repeatability, and long tool life.",
    fp3Industry: "Lock Industry",
    fpViewProject: "View project",
    learnMore: "Learn more",

    // How It Works
    hiwEyebrow: "How It Works",
    hiwTitle: "From inquiry to production-ready machine",
    hiwStep1: "Share your requirements",
    hiwStep1Desc: "Tell us about your application, materials, production volume, and quality targets. We listen first.",
    hiwStep2: "Engineering analysis",
    hiwStep2Desc: "Our team evaluates feasibility, defines welding parameters, and designs the right machine concept for your process.",
    hiwStep3: "Machine design & manufacturing",
    hiwStep3Desc: "We design and build your welding machine in-house — tailored to your specifications, tested before delivery.",
    hiwStep4: "Commissioning & support",
    hiwStep4Desc: "On-site installation, parameter fine-tuning, operator training, and ongoing technical support.",
    startProject: "Start Your Project",

    // Social Proof
    spEyebrow: "References",
    spTitle: "Trusted by industrial manufacturers worldwide",
    spYears: "Years of experience",
    spMachines: "Machines delivered",
    spCountries: "Countries served",
    spEngineering: "In-house engineering",
    spIndustries: "Industries we serve",
    indAutomotive: "Automotive",
    indWhiteGoods: "White Goods",
    indMetal: "Metal Processing",
    indBattery: "Battery Manufacturing",
    indElectrical: "Electrical Components",
    indHVAC: "HVAC",

    // FAQ
    faqEyebrow: "FAQ",
    faqTitle: "Frequently asked questions",
    faq1Q: "Are you a welding machine manufacturer or a service provider?",
    faq1A: "We are a resistance welding machine manufacturer. We design and build machines in-house and adapt them to specific applications to ensure reliable production results.",
    faq2Q: "Are your machines always premium-priced?",
    faq2A: "No. We design machines that balance performance, reliability, and investment level based on real production needs. From cost-optimized standard solutions to fully customized premium machines.",
    faq3Q: "What types of resistance welding do you cover?",
    faq3A: "Spot welding, projection welding, seam welding, micro welding, and battery welding applications — with AC and MFDC power source options.",
    faq4Q: "Can you upgrade or retrofit existing welding machines?",
    faq4A: "Yes. We offer control system upgrades, MFDC transitions, transformer replacements, and full mechanical refurbishment to extend machine life and improve performance.",
    faq5Q: "How long does it take from inquiry to machine delivery?",
    faq5A: "Typical lead times range from 8 to 20 weeks depending on machine complexity and customization level. We confirm timelines during the engineering phase.",
    faq6Q: "Do you provide on-site support?",
    faq6A: "Yes. We offer installation, commissioning, operator training, and ongoing technical support — directly from the engineering team that designed your machine.",
    faq7Q: "What is resistance welding?",
    faq7A: "Resistance welding is an industrial metal joining process where heat is generated by the electrical resistance of the material at the contact point of two metal surfaces. Electrical current is delivered through electrodes, which also provide mechanical pressure. Due to localized heating of the material, a weld joint is created without the use of additional filler material. This technology is widely used in the automotive industry, electrical component production, and various industrial applications.",
    faq8Q: "What is the difference between AC and MFDC resistance welding?",
    faq8A: "In conventional resistance welding, alternating current (AC) is used, while MFDC welding uses medium-frequency direct current. MFDC technology enables more precise welding current control, a more stable welding process, and greater energy efficiency. Due to these advantages, MFDC systems are frequently used in modern automated production lines.",
    faq9Q: "Where is micro resistance welding used?",
    faq9A: "Micro resistance welding is used for joining very small metallic components. The technology is particularly important in the electrical industry, production of electrical contacts, sensors, and battery systems. Thanks to precise energy control, it enables stable weld joints even on very small components.",
    faq10Q: "What is multi-strand wire compaction?",
    faq10A: "Multi-strand wire compaction is a process in which individual wires in an electrical conductor are joined into a compact structure using resistance welding. This process enables the creation of a stable electrical joint without the use of additional materials. The technology is frequently used in cable harness production for the automotive industry.",
    faq11Q: "What are the advantages of resistance welding?",
    faq11A: "Resistance welding enables very fast joining of metal components with high repeatability of results. The process does not require additional materials and is suitable for production automation. Due to these characteristics, resistance welding is one of the most important technologies in modern industrial production.",

    // Contact
    contactEyebrow: "Contact",
    contactTitle: "Let's discuss your welding application",
    contactSub: "Tell us about your project. We'll respond with a technical assessment and a clear path forward — no pressure, no catalog solutions.",
    contactSubmitted: "Inquiry submitted",
    contactSubmittedDesc: "Our engineering team will review your request and respond within 1–2 business days.",
    formName: "Full name *",
    formCompany: "Company *",
    formEmail: "Email *",
    formPhone: "Phone",
    formInquiryType: "Inquiry type *",
    formApplication: "Application (e.g., spot welding of automotive brackets)",
    formMessage: "Tell us about your project, requirements, or questions...",
    inqType1: "New welding machine",
    inqType2: "Upgrade / retrofit",
    inqType3: "Process optimization",
    inqType4: "Service / support",
    inqType5: "Consultation / feasibility study",

    // Map
    mapTitle: "Find us on the map",
    mapSub: "Kočevar, d.o.o. — Sončna cesta 10, Ločica ob Savinji",
    mapButton: "Open in Google Maps",

    // Newsletter
    nlTitle: "Stay ahead in welding technology",
    nlSub: "Join our newsletter for exclusive insights on resistance welding innovations, application tips, industry trends, and product updates — delivered straight to your inbox.",
    nlPopupTitle: "Don't miss welding insights",
    nlPopupSub: "Subscribe to receive expert tips, new machine announcements, and industry knowledge — free, no spam.",
    nlPlaceholder: "Your email address",
    nlButton: "Subscribe",
    nlSuccess: "Successfully subscribed!",
    nlSuccessDesc: "Thank you for subscribing. You'll receive our next newsletter soon.",
    nlConsent: "I agree to receive marketing emails and accept the",
    nlTermsLink: "Terms & Conditions",
    nlBenefit1: "Industry insights & trends",
    nlBenefit2: "New product announcements",
    nlBenefit3: "Unsubscribe anytime",
    nlNoSpam: "No spam. Unsubscribe at any time.",
    nlAlreadySubscribed: "This email is already subscribed.",
    rateLimited: "Too many attempts. Please try again later.",

    // About Us page
    aboutTitle: "About us",
    aboutHeadline: "We help you weld your product!",
    aboutIntro1: "Kočevar d.o.o. is a Slovenian engineering company specialized in resistance welding technologies for high-volume industrial production. With more than 50 years of experience, we develop, design and manufacture reliable welding equipment and systems that ensure stable processes, high repeatability and long operational lifetime.",
    aboutIntro2: "All equipment is developed and manufactured in Slovenia using high-quality European components. Our solutions range from manual welding workstations to semi-automatic and fully automated production systems tailored to the specific requirements of each customer.",
    aboutDetailTitle: "About Kočevar",
    aboutDetail1: "Kočevar d.o.o. is an engineering company with a long tradition in the development of resistance welding technologies. The company builds on more than five decades of experience in welding technology, machine design and industrial automation. From the earliest development of specialized resistance welding equipment to today's advanced production solutions, the company has continuously focused on reliability, precision and technological innovation.",
    aboutDetail2: "In the company Kočevar d.o.o. we are specialists for resistance welding in high-volume production. We develop, design and manufacture welding equipment that ensures stable processes and withstands demanding industrial environments. All systems are engineered and produced in Slovenia using high-quality materials and components sourced from the European Union. This allows us to maintain full control over the quality, reliability and long-term performance of our solutions.",
    aboutDetail3: "Our customers come from a wide range of industrial sectors where reliable joining technologies are essential for production. Kočevar acts as a strategic developmental supplier of welding solutions for industries such as automotive, aerospace, sheet metal processing, stainless steel processing, battery manufacturing and medical technology. Our role often goes beyond supplying equipment – we work closely with customers to develop optimized welding processes and integrate them into their production lines.",
    aboutDetail4: "A key strength of the company lies in the ability to provide complete turnkey solutions. We develop, design and manufacture the equipment ourselves and adapt each solution to the specific requirements of the customer's production environment. From the initial concept and feasibility studies to final installation and commissioning, Kočevar provides complete engineering support, technical assistance and long-term service.",
    aboutDetail5: "Our portfolio includes manual welding workstations, semi-automatic systems and fully automated production lines for resistance welding and micro-resistance welding applications. In addition to equipment manufacturing, we also support customers with welding tests, process development and feasibility studies to ensure optimal welding results before industrial implementation.",
    aboutDetail6: "Kočevar works closely with leading technology partners in the field of industrial automation and welding technology. As a partner of Bosch Rexroth and Harms & Wende, the company integrates advanced control systems, welding controllers and automation technologies into its solutions, ensuring high process stability and precise control of welding parameters.",
    aboutDetail7: "In addition to equipment development, the company also performs contract welding and compacting services, covering both small and large production series. This enables customers to test new products, validate production processes or outsource specialized welding tasks.",
    aboutDetail8: "Research, development and innovation are an important part of Kočevar's long-term strategy. Through continuous development of new welding technologies, specialized equipment and advanced production concepts, the company contributes to the advancement of modern manufacturing technologies and supports customers in achieving higher efficiency, reliability and product quality in their production processes.",
    aboutHistoryTitle: "History",
    aboutHistory1969: "The company KOČEVAR d.o.o., with the original name ELEKTROMEHANIKA – VARILNA TEHNIKA, was founded in the year of 1969, when a perspective engineer Mr. Jože Kočevar, with a small investment began with the development and production of welding power supplies.",
    aboutHistory1970: "The Yugoslavian market required large quantities of arc welding and resistance welding machines, so the company grew at a fast rate. By the end of the decade, the privately owned company had 25 employees and was expanding its range of products.",
    aboutHistory1980: "From 1984 onwards the company began manufacturing its first machine for micro-spot capacitor discharge welding. Our machines for micro resistance welding were being perfected in functionality and user friendliness from year to year.",
    aboutHistory1990: "After 1990's, our company, named KOČEVAR & THERMOTRON at the time, turned to the European markets. From the mass oriented production we turned to tailor made machines and solutions with a greater focus on quality.",
    aboutHistory2000: "In the new millennium our developing department was focusing especially on safety at work with our projection and seam welding machines. Later an automotive grade resistance pedestal welders were introduced to OEM, Tier 1 and Tier 2 manufacturers.",
    aboutHistory2010: "Newly developed machines, engineered and produced by company KOČEVAR d.o.o. are composed of high quality components, precisely produced using the newest technology and innovative solutions. Our welding machines are Industry 4.0 compatible and ready for Welding Automation.",
    aboutHistoryNowYear: "Today",
    aboutHistoryNowText: "Curious about what we're working on right now? Discover our latest projects, innovations and industry insights.",
    aboutHistoryNowCta: "See what's new",

    // Testimonials page
    testimonialsEyebrow: "Testimonials",
    testimonialsTitle: "What our clients say",
    testimonialsSub: "Hear directly from the industrial manufacturers who trust Kočevar welding machines in their production lines.",
    testimonial1Quote: "Die Anlage funktioniert gut, nach den Optionen denke ich ist diese Optimal für unseren Bedarf, danke nochmals für die gute und partnerschaftliche Zusammenarbeit.",
    testimonial1Closing: "Mit freundlichem Gruß",
    testimonial2Quote: "In the name of Kirchhoff Automotive, I would like to thanks to the team from Kocevar for all the effort and support from the last week, so that we could achieve the important goal of delivering today the first parts produced in our plant. As all projects, there was more positive and less positive phases, but with the commitment and capacity of all the involved ones, we had made possible what for some was \"almost impossible\". Thanks once more to all.",
    testimonial2Closing: "Mit freundlichen Grüßen / Best regards",

    // Users & Clients page
    ucEyebrow: "References",
    ucTitle: "Users & Clients",
    ucSub: "Trusted by leading manufacturers across automotive, robotics, gastronomy, and other industries worldwide.",
    ucAutomotive: "Automotive Industry",
    ucRobotics: "Robotics Industry",
    ucGastronomy: "Gastronomy",
    ucOther: "Other Industries",
    ucCtaTitle: "Join our trusted partners",
    ucCtaSub: "Let's discuss how Kočevar welding machines can improve your production process.",

    // Product CTA
    productCtaInterested: "Interested in",
    productCtaQuestion: "Get in touch with our team for a tailored solution.",

    // Footer
    footerRights: "All rights reserved.",
    footerPrivacy: "Privacy Policy",

    // Consent
    formConsent: "I agree that my personal data (name, company, email) may be processed for the purpose of responding to my inquiry and for marketing purposes.",
    formConsentLink: "Read Terms & Privacy Policy",
  },
  sl: {
    // Nav
    navHome: "Domov",
    navSolutions: "Industrijske rešitve",
    navServices: "Rešitve",
    offerServices: "Storitve",
    offerProducts: "Standardni produkti",
    offerSpecialMachines: "Namenski stroji",
    offerSpareParts: "Rezervni deli",
    offerConsumables: "Potrošni material",
    offerGastroMedicine: "Gastronomija & Medicina",
    navHowItWorks: "Kako deluje",
    navReferences: "Partnerji",
    refProjects: "Projekti",
    refUsers: "Uporabniki / Stranke",
    refTestimonials: "Pričevanja",
    refPartners: "Partnerji",
    navKnowledge: "Novice",
    navFAQ: "FAQ",
    navAbout: "O nas",
    navContact: "Kontakt",
    sendInquiry: "Pošlji povpraševanje",
    solGastronomy: "Gastronomija in medicina",
    solAutomotive: "Avtomobilska industrija",
    solRailway: "Železniška industrija",
    solElectronics: "Industrija elektronike",
    solFences: "Mreže in ograje",
    solLocks: "Ključavnice",
    solRobotics: "Robotizacija",
    solWhiteGoods: "Bela tehnika",

    // Solution sub-items
    solGastSinks: "Stroj za točkovno varjenje pomivalnih korit v pulte",
    solGastPillow: "Stroj za šivno varjenje blazinastih plošč",
    solRailSpotGuns: "Robotske točkovne varilne pištole",
    solElecBattery: "Mikro-točkovni varilnik za baterije",
    solLocksWelding: "Stroj za uporovno varjenje ključavnic",
    solRoboticSpotGuns: "Robotske točkovne varilne pištole",
    solTensionRingWelding: "Varjenje zateznih obročev",
    solBarrelHandleWelding: "Stroj za varjenje ročic na sode",
    solTappingBarrelWelding: "Stroj za varjenje natočnih sodov",

    // Product categories & sub-items
    prodSpotWelding: "Točkovno varjenje",
    prodSpotWeldingCategory: "Točkovno varjenje",
    prodPedestalWelders: "Industrijski steberni varilniki",
    prodMultiSpot: "Stroj za več-točkovno varjenje",
    prodProjectionWelding: "Bradavičasto varjenje",
    prodProjectionWelders: "Industrijski bradavičasti varilniki",
    prodSeamWelding: "Šivno varjenje",
    prodSeamWelders: "Industrijski šivni varilniki",
    prodMicroWelding: "Mikro varjenje",
    prodWeldingHeads: "Varilne glave",
    prodWeldingPincers: "Varilne klešče",
    prodCompactingTools: "Orodja za stiskanje",
    prodBatteryProcessing: "Baterije in akumulatorji",
    prodWorkstations: "Delovna mesta",

    // Hero
    heroEyebrow: "Proizvajalec strojev za uporovno varjenje",
    heroTitle1: "Varilni stroji, zasnovani za ",
    heroTitleHighlight: "vaš proces",
    heroSub: "Od cenovno optimiziranih varilnih strojev do popolnoma prilagojenih premium rešitev — zasnovanih za vaš proces, ne iz kataloga.",
    viewSolutions: "Oglejte si rešitve",

    // Benefits
    benefitsEyebrow: "Zakaj Kočevar",
    benefitsTitle: "Rezultati, ki štejejo v proizvodnji",
    benefit1Title: "Stabilna kakovost zvarov",
    benefit1Desc: "Dosledni, ponovljivi rezultati varjenja v vsakem proizvodnem ciklu — manj izmeta in popravljanja.",
    benefit2Title: "Aplikativno vodena zasnova",
    benefit2Desc: "Vsak stroj je zasnovan okoli vaše specifične varilne aplikacije, materialov in proizvodnih zahtev.",
    benefit3Title: "Ponovljivost procesa",
    benefit3Desc: "Natančen nadzor varilnih parametrov zagotavlja enakomeren rezultat, izmeno za izmeno.",
    benefit4Title: "Zanesljiva lokalna podpora",
    benefit4Desc: "Lastni inženiring in tehnični servis — hiter odziv, brez posrednikov.",

    // Featured Projects
    fpEyebrow: "Izpostavljeni projekti",
    fpTitle: "Resnične varilne rešitve v praksi",
    fp1Title: "Točkovno varjenje pomivalnih korit v delovne pulte",
    fp1Desc: "Posebej razviti varilni stroji za gastronomsko industrijo — integracija nerjavečih pomivalnih korit v profesionalne delovne pulte s preciznim šivnim in točkovnim varjenjem.",
    fp1Industry: "Gastronomija in medicina",
    fp2Title: "Robotske uporovne točkovne varilne pištole",
    fp2Desc: "Visoko zmogljive varilne pištole za robotske celice v avtomobilski in železniški industriji — zasnovane za hitrost, ponovljivost in minimalno vzdrževanje.",
    fp2Industry: "Avtomobilska in železniška",
    fp3Title: "Varjenje ključavnic",
    fp3Desc: "Po meri zasnovani uporovni varilni stroji za visokoserijsko proizvodnjo ključavnic in zapiral — zasnovani za natančnost, ponovljivost in dolgo življenjsko dobo orodja.",
    fp3Industry: "Industrija ključavnic",
    fpViewProject: "Ogled projekta",
    learnMore: "Več informacij",

    // How It Works
    hiwEyebrow: "Kako deluje",
    hiwTitle: "Od povpraševanja do proizvodno pripravljenega stroja",
    hiwStep1: "Delite svoje zahteve",
    hiwStep1Desc: "Povejte nam o svoji aplikaciji, materialih, obsegu proizvodnje in ciljih kakovosti. Najprej poslušamo.",
    hiwStep2: "Inženirska analiza",
    hiwStep2Desc: "Naša ekipa oceni izvedljivost, določi varilne parametre in oblikuje pravi koncept stroja za vaš proces.",
    hiwStep3: "Zasnova in izdelava stroja",
    hiwStep3Desc: "Stroj zasnujemo in izdelamo v lastni proizvodnji — prilagojen vašim specifikacijam, preizkušen pred dostavo.",
    hiwStep4: "Zagon in podpora",
    hiwStep4Desc: "Namestitev na lokaciji, fino nastavljanje parametrov, usposabljanje operaterjev in stalna tehnična podpora.",
    startProject: "Začnite svoj projekt",

    // Social Proof
    spEyebrow: "Dosežki",
    spTitle: "Zaupajo nam industrijski proizvajalci po vsem svetu",
    spYears: "Let izkušenj",
    spMachines: "Dostavljenih strojev",
    spCountries: "Držav",
    spEngineering: "Lastni inženiring",
    spIndustries: "Industrije, ki jih oskrbujemo",
    indAutomotive: "Avtomobilska industrija",
    indWhiteGoods: "Bela tehnika",
    indMetal: "Obdelava kovin",
    indBattery: "Proizvodnja baterij",
    indElectrical: "Električne komponente",
    indHVAC: "HVAC",

    // FAQ
    faqEyebrow: "Pogosta vprašanja",
    faqTitle: "Pogosto zastavljena vprašanja",
    faq1Q: "Ali ste proizvajalec varilnih strojev ali ponudnik storitev?",
    faq1A: "Smo proizvajalec strojev za uporovno varjenje. Stroje projektiramo in izdelujemo v lastni proizvodnji ter jih prilagajamo specifičnim aplikacijam za zagotovitev zanesljivih proizvodnih rezultatov.",
    faq2Q: "Ali so vaši stroji vedno premium cenovno rangirani?",
    faq2A: "Ne. Stroje zasnujemo tako, da uravnotežijo zmogljivost, zanesljivost in investicijsko raven na podlagi realnih proizvodnih potreb.",
    faq3Q: "Katere vrste uporovnega varjenja pokrivate?",
    faq3A: "Točkovno, bradavičasto, šivno, mikro varjenje in aplikacije varjenja baterij — z možnostjo AC in MFDC virov toka.",
    faq4Q: "Ali lahko nadgradite ali prenovite obstoječe varilne stroje?",
    faq4A: "Da. Ponujamo nadgradnje krmilnih sistemov, prehode na MFDC, zamenjave transformatorjev in celovito mehansko obnovo za podaljšanje življenjske dobe in izboljšanje zmogljivosti.",
    faq5Q: "Koliko časa traja od povpraševanja do dostave stroja?",
    faq5A: "Tipični dobavni roki znašajo od 8 do 20 tednov, odvisno od kompleksnosti stroja in stopnje prilagoditve. Roke potrdimo v fazi inženiringa.",
    faq6Q: "Ali nudite podporo na lokaciji?",
    faq6A: "Da. Ponujamo namestitev, zagon, usposabljanje operaterjev in stalno tehnično podporo — neposredno s strani inženirske ekipe, ki je zasnovala vaš stroj.",
    faq7Q: "Kaj je uporovno varjenje?",
    faq7A: "Uporovno varjenje je industrijski postopek spajanja kovin, pri katerem toplota nastane zaradi električnega upora materiala na stiku dveh kovinskih površin. Električni tok se dovede preko elektrod, ki hkrati zagotavljajo tudi mehanski pritisk. Zaradi lokalnega segrevanja materiala nastane zvarni spoj brez uporabe dodatnega dodajnega materiala. Ta tehnologija se pogosto uporablja v avtomobilski industriji, proizvodnji električnih komponent in različnih industrijskih aplikacijah.",
    faq8Q: "Kakšna je razlika med AC in MFDC uporovnim varjenjem?",
    faq8A: "Pri klasičnem uporovnem varjenju se uporablja izmenični tok (AC), medtem ko MFDC varjenje uporablja srednjefrekvenčni enosmerni tok. MFDC tehnologija omogoča bolj natančen nadzor varilnega toka, stabilnejši proces varjenja in večjo energijsko učinkovitost. Zaradi teh prednosti se MFDC sistemi pogosto uporabljajo v sodobnih avtomatiziranih proizvodnih linijah.",
    faq9Q: "Kje se uporablja mikro uporovno varjenje?",
    faq9A: "Mikro uporovno varjenje se uporablja pri spajanju zelo majhnih kovinskih komponent. Tehnologija je posebej pomembna v elektrotehnični industriji, proizvodnji električnih kontaktov, senzorjev in baterijskih sistemov. Zaradi natančnega nadzora energije omogoča stabilne zvarne spoje tudi pri zelo majhnih komponentah.",
    faq10Q: "Kaj je kompaktiranje večžilnih vodnikov?",
    faq10A: "Kompaktiranje večžilnih vodnikov je postopek, pri katerem se posamezne žice v električnem vodniku z uporabo uporovnega varjenja povežejo v kompaktno strukturo. Takšen postopek omogoča izdelavo stabilnega električnega spoja brez uporabe dodatnih materialov. Tehnologija se pogosto uporablja v proizvodnji kabelskih snopov za avtomobilsko industrijo.",
    faq11Q: "Kakšne so prednosti uporovnega varjenja?",
    faq11A: "Uporovno varjenje omogoča zelo hitro spajanje kovinskih komponent in visoko ponovljivost rezultatov. Proces ne zahteva dodatnih materialov in je primeren za avtomatizacijo proizvodnje. Zaradi teh lastnosti je uporovno varjenje ena najpomembnejših tehnologij sodobne industrijske proizvodnje.",

    // Contact
    contactEyebrow: "Kontakt",
    contactTitle: "Pogovorimo se o vaši varilni aplikaciji",
    contactSub: "Povejte nam o svojem projektu. Odgovorili vam bomo s tehnično oceno in jasno potjo naprej — brez pritiska, brez kataloških rešitev.",
    contactSubmitted: "Povpraševanje oddano",
    contactSubmittedDesc: "Naša inženirska ekipa bo pregledala vašo zahtevo in odgovorila v 1–2 delovnih dneh.",
    formName: "Ime in priimek *",
    formCompany: "Podjetje *",
    formEmail: "E-pošta *",
    formPhone: "Telefon",
    formInquiryType: "Vrsta povpraševanja *",
    formApplication: "Aplikacija (npr. točkovno varjenje avtomobilskih nosilcev)",
    formMessage: "Povejte nam o svojem projektu, zahtevah ali vprašanjih...",
    inqType1: "Nov varilni stroj",
    inqType2: "Nadgradnja / prenova",
    inqType3: "Optimizacija procesa",
    inqType4: "Servis / podpora",
    inqType5: "Konzultacija / študija izvedljivosti",

    // Map
    mapTitle: "Poiščite nas na zemljevidu",
    mapSub: "Kočevar, d.o.o. — Sončna cesta 10, Ločica ob Savinji",
    mapButton: "Odpri v Google Maps",

    // Newsletter
    nlTitle: "Bodite v koraku z varilno tehnologijo",
    nlSub: "Pridružite se našemu novičniku za ekskluzivne vpoglede v inovacije uporovnega varjenja, aplikativne nasvete, industrijske trende in novosti izdelkov — neposredno v vaš e-poštni nabiralnik.",
    nlPopupTitle: "Ne zamudite varilnih novosti",
    nlPopupSub: "Prijavite se za prejemanje strokovnih nasvetov, obvestil o novih strojih in industrijskega znanja — brezplačno, brez neželene pošte.",
    nlPlaceholder: "Vaš e-poštni naslov",
    nlButton: "Prijava",
    nlSuccess: "Uspešno prijavljeni!",
    nlSuccessDesc: "Hvala za prijavo. Naš naslednji novičnik boste prejeli kmalu.",
    nlConsent: "Strinjam se s prejemanjem marketinških e-poštnih sporočil in sprejemam",
    nlTermsLink: "Splošne pogoje poslovanja",
    nlBenefit1: "Industrijski vpogledi in trendi",
    nlBenefit2: "Obvestila o novih izdelkih",
    nlBenefit3: "Odjava kadarkoli",
    nlNoSpam: "Brez neželene pošte. Odjava kadarkoli.",
    nlAlreadySubscribed: "Ta e-poštni naslov je že prijavljen.",
    rateLimited: "Preveč poskusov. Poskusite znova pozneje.",

    // About Us page
    aboutTitle: "O nas",
    aboutHeadline: "Pomagamo vam zvariti vaš izdelek!",
    aboutIntro1: "Kočevar d.o.o. je slovensko inženirsko podjetje, specializirano za tehnologije uporovnega varjenja za visoko-serijsko industrijsko proizvodnjo. Z več kot 50-letnimi izkušnjami razvijamo, projektiramo in proizvajamo zanesljivo varilno opremo in sisteme, ki zagotavljajo stabilne procese, visoko ponovljivost in dolgo življenjsko dobo.",
    aboutIntro2: "Vsa oprema je razvita in izdelana v Sloveniji z uporabo visokokakovostnih evropskih komponent. Naše rešitve segajo od ročnih varilnih delovnih postaj do polavtomatskih in popolnoma avtomatiziranih proizvodnih sistemov, prilagojenih specifičnim zahtevam vsakega kupca.",
    aboutDetailTitle: "O podjetju Kočevar",
    aboutDetail1: "Kočevar d.o.o. je inženirsko podjetje z dolgo tradicijo na področju razvoja tehnologij uporovnega varjenja. Podjetje gradi na več kot petdesetletnih izkušnjah v varilni tehnologiji, projektiranju strojev in industrijski avtomatizaciji. Od najzgodnejšega razvoja specializirane opreme za uporovno varjenje do današnjih naprednih proizvodnih rešitev se podjetje nenehno osredotoča na zanesljivost, natančnost in tehnološke inovacije.",
    aboutDetail2: "V podjetju Kočevar d.o.o. smo specialisti za uporovno varjenje v visoko-serijski proizvodnji. Razvijamo, projektiramo in proizvajamo varilno opremo, ki zagotavlja stabilne procese in vzdrži v zahtevnih industrijskih okoljih. Vsi sistemi so zasnovani in izdelani v Sloveniji z uporabo visokokakovostnih materialov in komponent iz Evropske unije. To nam omogoča popoln nadzor nad kakovostjo, zanesljivostjo in dolgoročno zmogljivostjo naših rešitev.",
    aboutDetail3: "Naše stranke prihajajo iz širokega spektra industrijskih sektorjev, kjer so zanesljive tehnologije spajanja ključne za proizvodnjo. Kočevar nastopa kot strateški razvojni dobavitelj varilnih rešitev za panoge, kot so avtomobilska industrija, letalska industrija, obdelava pločevine, obdelava nerjavečega jekla, proizvodnja baterij in medicinska tehnologija. Naša vloga pogosto presega dobavo opreme – tesno sodelujemo s strankami pri razvoju optimiziranih varilnih procesov in njihovi integraciji v proizvodne linije.",
    aboutDetail4: "Ključna prednost podjetja je sposobnost zagotavljanja celovitih rešitev na ključ. Opremo razvijamo, projektiramo in izdelujemo sami ter vsako rešitev prilagodimo specifičnim zahtevam strankinega proizvodnega okolja. Od začetnega koncepta in študij izvedljivosti do končne instalacije in zagona Kočevar zagotavlja celovito inženirsko podporo, tehnično pomoč in dolgoročni servis.",
    aboutDetail5: "Naš portfelj vključuje ročne varilne delovne postaje, polavtomatske sisteme in popolnoma avtomatizirane proizvodne linije za uporovno varjenje in mikro uporovno varjenje. Poleg proizvodnje opreme stranke podpiramo tudi z varilnimi testi, razvojem procesov in študijami izvedljivosti za zagotovitev optimalnih varilnih rezultatov pred industrijsko implementacijo.",
    aboutDetail6: "Kočevar tesno sodeluje z vodilnimi tehnološkimi partnerji na področju industrijske avtomatizacije in varilne tehnologije. Kot partner podjetij Bosch Rexroth in Harms & Wende podjetje v svoje rešitve integrira napredne krmilne sisteme, varilne krmilnike in avtomatizacijske tehnologije, kar zagotavlja visoko procesno stabilnost in natančen nadzor varilnih parametrov.",
    aboutDetail7: "Poleg razvoja opreme podjetje izvaja tudi pogodbeno varjenje in kompaktiranje, tako za male kot velike proizvodne serije. To strankam omogoča preizkušanje novih izdelkov, validacijo proizvodnih procesov ali oddajo specializiranih varilnih nalog.",
    aboutDetail8: "Raziskave, razvoj in inovacije so pomemben del dolgoročne strategije podjetja Kočevar. S stalnim razvojem novih varilnih tehnologij, specializirane opreme in naprednih proizvodnih konceptov podjetje prispeva k napredku sodobnih proizvodnih tehnologij ter podpira stranke pri doseganju višje učinkovitosti, zanesljivosti in kakovosti izdelkov v njihovih proizvodnih procesih.",
    aboutHistoryTitle: "Zgodovina",
    aboutHistory1969: "Podjetje KOČEVAR d.o.o., s prvotnim imenom ELEKTROMEHANIKA – VARILNA TEHNIKA, je bilo ustanovljeno leta 1969, ko je perspektiven inženir g. Jože Kočevar z majhno investicijo začel z razvojem in proizvodnjo varilnih virov toka.",
    aboutHistory1970: "Jugoslovanski trg je zahteval velike količine strojev za obločno in uporovno varjenje, zato je podjetje hitro raslo. Do konca desetletja je zasebno podjetje imelo 25 zaposlenih in širilo svojo paleto izdelkov.",
    aboutHistory1980: "Od leta 1984 naprej je podjetje začelo proizvajati svoj prvi stroj za mikro-točkovno kondenzatorsko varjenje. Naši stroji za mikro uporovno varjenje so bili iz leta v leto izpopolnjevani v funkcionalnosti in uporabniški prijaznosti.",
    aboutHistory1990: "Po letu 1990 se je naše podjetje, takrat imenovano KOČEVAR & THERMOTRON, usmerilo na evropske trge. Od množične proizvodnje smo prešli na stroje in rešitve po meri z večjim poudarkom na kakovosti.",
    aboutHistory2000: "V novem tisočletju se je naš razvojni oddelek osredotočal predvsem na varnost pri delu z našimi bradavičastimi in šivnimi varilnimi stroji. Pozneje so bili predstavljeni avtomobilski uporovni steberni varilniki za OEM, Tier 1 in Tier 2 proizvajalce.",
    aboutHistory2010: "Novo razviti stroji, zasnovani in proizvedeni v podjetju KOČEVAR d.o.o., so sestavljeni iz visokokakovostnih komponent, natančno izdelanih z uporabo najnovejše tehnologije in inovativnih rešitev. Naši varilni stroji so združljivi z Industrijo 4.0 in pripravljeni na avtomatizacijo varjenja.",
    aboutHistoryNowYear: "Danes",
    aboutHistoryNowText: "Vas zanima, na čem trenutno delamo? Odkrijte naše najnovejše projekte, inovacije in vpoglede v industrijo.",
    aboutHistoryNowCta: "Odkrijte novosti",

    // Testimonials page
    testimonialsEyebrow: "Pričevanja",
    testimonialsTitle: "Kaj pravijo naše stranke",
    testimonialsSub: "Slišite neposredno od industrijskih proizvajalcev, ki zaupajo Kočevarjevim varilnim strojem v svojih proizvodnih linijah.",
    testimonial1Quote: "Die Anlage funktioniert gut, nach den Optionen denke ich ist diese Optimal für unseren Bedarf, danke nochmals für die gute und partnerschaftliche Zusammenarbeit.",
    testimonial1Closing: "Mit freundlichem Gruß",
    testimonial2Quote: "In the name of Kirchhoff Automotive, I would like to thanks to the team from Kocevar for all the effort and support from the last week, so that we could achieve the important goal of delivering today the first parts produced in our plant. As all projects, there was more positive and less positive phases, but with the commitment and capacity of all the involved ones, we had made possible what for some was \"almost impossible\". Thanks once more to all.",
    testimonial2Closing: "Mit freundlichen Grüßen / Best regards",

    // Users & Clients page
    ucEyebrow: "Reference",
    ucTitle: "Uporabniki in stranke",
    ucSub: "Zaupajo nam vodilni proizvajalci iz avtomobilske, robotske, gastronomske in drugih industrij po vsem svetu.",
    ucAutomotive: "Avtomobilska industrija",
    ucRobotics: "Robotska industrija",
    ucGastronomy: "Gastronomija",
    ucOther: "Druge industrije",
    ucCtaTitle: "Pridružite se našim zaupanja vrednim partnerjem",
    ucCtaSub: "Pogovorimo se o tem, kako lahko Kočevarjevi varilni stroji izboljšajo vaš proizvodni proces.",

    // Product CTA
    productCtaInterested: "Vas zanimajo",
    productCtaQuestion: "Kontaktirajte našo ekipo za prilagojeno rešitev.",

    // Footer
    footerRights: "Vse pravice pridržane.",
    footerPrivacy: "Politika zasebnosti",

    // Consent
    formConsent: "Soglašam, da se moji osebni podatki (ime, podjetje, e-pošta) obdelujejo za namene odgovora na povpraševanje in v marketinške namene.",
    formConsentLink: "Preberi Splošne pogoje in Politiko zasebnosti",
  },
} as const;

type Translations = { [K in keyof typeof translations.en]: string };

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "en",
  setLang: () => {},
  t: translations.en,
});

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem("preferred-lang");
    return saved === "sl" ? "sl" : "en";
  });

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem("preferred-lang", newLang);
  };

  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
